import { useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DocumentViewer from '../components/DocumentViewer';

// A document in its own browser window — /case/:caseId/view/:docId.
//
// Opened by the viewer's "Open in a new window" button, so the document can
// sit beside the inventory or on a second screen while the list stays where
// it was. The viewer is the whole window (.dv-standalone in app.css); the
// help orb stays out of it. Closing the viewer closes the window; when the
// browser will not close a window it did not open itself (the address was
// typed or shared), the window goes to the case's inventory instead.
// `?page=` and `?t=` carry the position the viewer was on.
export default function DocumentWindow() {
  const { caseId, docId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const page = params.get('page');
  const t = params.get('t');
  const initialPage = page ? Math.max(1, parseInt(page, 10) || 1) : null;
  const startAt = t != null && t !== '' && Number.isFinite(Number(t)) ? Number(t) : null;

  const close = useCallback(() => {
    window.close();
    // Still here: the browser kept the window — go to the case instead.
    setTimeout(() => navigate(`/case/${caseId}/inventory`, { replace: true }), 150);
  }, [caseId, navigate]);

  // "Open the original" (an excerpt, a redacted copy) stays in this window.
  const openDocument = useCallback((id, opts = null) => {
    const s = Number(opts?.startAt);
    navigate(`/case/${caseId}/view/${id}${Number.isFinite(s) ? `?t=${s}` : ''}`);
  }, [caseId, navigate]);

  return (
    <div className="dv-standalone" data-testid="document-window">
      <DocumentViewer
        key={docId}
        documentId={docId}
        standalone
        startAt={startAt}
        initialPage={initialPage}
        onClose={close}
        onOpenDocument={openDocument}
      />
    </div>
  );
}
